/** @format */

import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
  _$,
  Card,
  Button,
  Table,
  TableColumn,
  Pagination,
  Icon,
  Checkbox,
  TableSortValue,
  Select,
  Option,
  Message
} from '@king-design/react'
import {
  MonitorSearch,
  TableAction,
  UserInfo,
  CustomPageContainer,
  IconButton
} from 'src/components'
import dayjs from 'dayjs'
import { useHistory, useLocation } from 'react-router'
import { getSceneList } from 'src/api/ApiEventOpsScene'
import { useAsyncFn, useCopyToClipboard } from 'react-use'
import { getStatus } from '../Trigger/container'
import DeleteDialog from './components/DeleteDialog'
import VersionDrawer from './components/VersionDrawer'
import { SceneRecordItemProp, copyScene } from './container'
import { initPage, PageProps } from 'src/utils/interface'
interface SearchProps extends PageProps {
  RuleName: string
  IsPublic: '' | 1 | -1
  AllowLaunch: boolean
}
const initSearch: SearchProps = {
  ...initPage,
  RuleName: '',
  IsPublic: '',
  AllowLaunch: false
}
function useQuery() {
  return new URLSearchParams(useLocation().search)
}

// 抽屉基础信息
export interface DrawerProps {
  visible: boolean
  record: SceneRecordItemProp
}
export const initDrawer: DrawerProps = {
  visible: false,
  record: undefined
}

const TriggerPage: React.FC<{ className?: string }> = ({ className }) => {
  const history = useHistory()
  const query = useQuery()
  const initAllowLounch = query.get('allowLaunch') || 'false'
  const initName = query.get('name') || ''
  const [search, setSearch] = useState<SearchProps>({
    ...initSearch,
    AllowLaunch: initAllowLounch === 'true',
    RuleName: initName ? decodeURIComponent(initName) : ''
  })
  const [sort, setSort] = useState<TableSortValue>({})
  // 版本抽屉
  const [versionVisible, setVersionVisible] = useState(false)
  const [versionRecord, setVersionRecord] = useState<SceneRecordItemProp>(undefined)
  // 删除二次确认弹窗
  const [deleteDialog, setDeleteDialog] = useState<DrawerProps>(initDrawer)
  // 复制
  const [, copyToClipboard] = useCopyToClipboard()
  // 获取列表
  const [{ value: sceneList }, fetchSceneList] = useAsyncFn(
    async (params?) => {
      const result = await getSceneList({
        ...params,
        Offset: (search.Page - 1) * search.PageSize,
        Limit: search.PageSize,
        IsPublic: search?.IsPublic ? search.IsPublic : undefined,
        AllowLaunch: search.AllowLaunch,
        Search: search.RuleName,
        Order: sort?.key && sort?.type ? `${sort?.key} ${sort?.type}` : undefined
      })
      return result
    },
    [search, sort]
  )

  useEffect(() => {
    fetchSceneList()
  }, [search, sort])

  return (
    <div className={className}>
      <CustomPageContainer title={_$('自动化场景')}>
        <Card type="none">
          <div className="search_row">
            <div className="ksc-sapce">
              <Button
                type="primary"
                onClick={() => {
                  history.push('/monitor/eventops/scene/detail')
                }}
              >
                <Icon className="ion-plus" />
                {_$('新建场景')}
              </Button>
              <Select
                value={search.IsPublic}
                slotPrefix={`${_$('类型')}：`}
                clearable
                onChangeValue={value => {
                  setSearch({
                    ...search,
                    IsPublic: value,
                    Page: 1
                  })
                }}
              >
                <Option value="">{_$('全部')}</Option>
                <Option value={1}>{_$('系统场景')}</Option>
                <Option value={-1}>{_$('自定义场景')}</Option>
              </Select>
              <Checkbox
                value={search.AllowLaunch}
                onChangeValue={value => {
                  history.push(`/monitor/eventops/scene?allowLaunch=${value}`)
                  setSearch({ ...search, Page: 1, AllowLaunch: !!value })
                }}
              >
                {_$('可执行')}
              </Checkbox>
            </div>
            <div className="ksc-sapce">
              <MonitorSearch
                placeholder={_$('请输入场景名称，支持模糊搜索')}
                value={search.RuleName}
                onChange={value => {
                  setSearch({
                    ...search,
                    Page: 1,
                    RuleName: value
                  })
                }}
              />
              <IconButton type="refresh" title={_$('刷新')} onClick={() => fetchSceneList()} />
            </div>
          </div>
          <Table
            data={sceneList?.Info || []}
            animation={false}
            rowKey={record => record.id}
            checkType="none"
            sort={sort}
            onChangeSort={sort => {
              setSearch({
                ...search,
                Page: 1
              })
              setSort(sort)
            }}
          >
            <TableColumn key="ID" title="ID" width="80" sortable />
            <TableColumn key="Name" title={_$('场景名称')} width="200" sortable />
            <TableColumn
              key="IsPublic"
              title={_$('类型')}
              width="100"
              sortable
              slotTemplate={([{ IsPublic }]) => {
                return IsPublic === 1 ? _$('系统场景') : _$('自定义场景')
              }}
            />
            <TableColumn
              key="Creator"
              title={_$('操作人')}
              width="100"
              sortable
              slotTemplate={([{ Creator }]) => {
                return <UserInfo name={Creator} />
              }}
            />
            <TableColumn
              key="PublishedAt"
              title={_$('发布时间')}
              width="180"
              sortable
              slotTemplate={([{ PublishedAt }]) => {
                return PublishedAt ? dayjs(PublishedAt).format('YYYY-MM-DD HH:mm:ss') : '-'
              }}
            />
            <TableColumn
              key="Version"
              title={_$('版本')}
              width="80"
              slotTemplate={([{ Version }]) => {
                return Version || '-'
              }}
            />
            <TableColumn
              key="TriggerCount"
              title={_$('触发规则')}
              width="80"
              slotTemplate={([{ TriggerCount, Sign }]) => {
                return TriggerCount > 0 ? (
                  <a
                    onClick={() => {
                      history.push(`/monitor/eventops/trigger?actionSign=${Sign}`)
                    }}
                  >
                    {TriggerCount}
                  </a>
                ) : (
                  <span>0</span>
                )
              }}
            />
            <TableColumn
              key="Status"
              title={_$('状态')}
              fixed="right"
              width="100"
              sortable
              slotTemplate={([{ Status }]) => {
                return getStatus(Status)
              }}
            />
            <TableColumn
              key="operations"
              title={_$('操作')}
              fixed="right"
              width="180"
              slotTemplate={([record]) => {
                const { Status, FormSchema, IsPublic } = record
                const formSchemaJson = JSON.parse(FormSchema)
                return (
                  <>
                    <TableAction
                      actions={[
                        {
                          key: 'view',
                          label: _$('查看'),
                          onClick: () => {
                            history.push(`/monitor/eventops/scene/detail/${record.Sign}`)
                          }
                        },
                        {
                          key: 'version',
                          label: _$('版本'),
                          disabled: IsPublic !== -1,
                          onClick: () => {
                            setVersionRecord(record)
                            setVersionVisible(true)
                          }
                        },
                        {
                          key: 'execute',
                          label: _$('执行'),
                          disabled:
                            IsPublic !== -1 ||
                            Status !== 2 ||
                            !formSchemaJson ||
                            !formSchemaJson?.schema?.properties,
                          onClick: () => {
                            history.push(`/monitor/eventops/scene/launch/${record.Sign}`)
                          }
                        },
                        {
                          key: 'copy',
                          label: _$('复制'),
                          onClick: async () => {
                            const copyResult = await copyScene(record.ID)
                            if (copyResult) {
                              copyToClipboard(JSON.stringify(copyResult))
                              Message.success(_$('已复制到粘贴板，请点击【新建场景】按钮去导入'))
                            }
                          }
                        },
                        {
                          key: 'delete',
                          label: _$('删除'),
                          disabled: IsPublic !== -1,
                          onClick: () => {
                            setDeleteDialog({
                              visible: true,
                              record
                            })
                          }
                        }
                      ]}
                    />
                  </>
                )
              }}
            />
          </Table>
          <Pagination
            value={search.Page}
            limit={search.PageSize}
            total={sceneList?.Total || 0}
            size="small"
            onChangeValue={value => {
              setSearch({
                ...search,
                Page: value
              })
            }}
            onChangeLimit={limit => {
              setSearch({
                ...search,
                PageSize: limit
              })
            }}
          />
        </Card>
      </CustomPageContainer>
      {/* 删除二次确认弹窗 */}
      <DeleteDialog
        {...deleteDialog}
        onClose={update => {
          setDeleteDialog(initDrawer)
          if (update) {
            setSearch({
              ...search,
              Page: 1
            })
          }
        }}
      />
      {/* 版本列表抽屉 */}
      <VersionDrawer
        visible={versionVisible}
        record={versionRecord}
        onClose={() => setVersionVisible(false)}
      />
    </div>
  )
}

export default styled(TriggerPage)`
  .search_row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    .ksc-sapce {
      display: flex;
      align-items: center;
      gap: 16px;
    }
  }
  .k-pagination {
    margin-top: 16px;
    text-align: center;
  }
`
